/**
 * Sales by Item page — NetSuite sales data broken down by item for a single portfolio company.
 * In live mode: requires authentication via MsalAuthenticationTemplate.
 * In demo mode: loads data without authentication (uses demo endpoint with live NS fallback).
 */

import { useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { InteractionStatus, InteractionType } from "@azure/msal-browser";
import { powerbiRequest } from "../authConfig";
import { Loading } from "../ui-components/Loading";
import { LoadingNotice } from "../ui-components/LoadingNotice";
import { ErrorComponent } from "../ui-components/ErrorComponent";
import { MetricCard } from "../ui-components/MetricCard";
import { PeriodSelector } from "../ui-components/PeriodSelector";
import { SalesByItemChart } from "../ui-components/SalesByItemChart";
import { SalesByItemTable } from "../ui-components/SalesByItemTable";
import { PortcoSubNav } from "../ui-components/PortcoSubNav";
import { isDemoMode, SalesPeriod } from "../utils/PowerBIApiCall";
import {
  useSalesByItemData,
  groupItemsByCategory,
  ProcessedSalesItem,
  SalesByItemSummary,
  SalesGroupBy,
} from "../hooks/useSalesByItemData";
import { formatCurrencyCompact as formatCurrency } from "../utils/format";

// =============================================================================
// Shared UI — pure presentational component, no MSAL dependency
// =============================================================================

const groupByOptions: { key: SalesGroupBy; label: string }[] = [
  { key: "item", label: "Item" },
  { key: "category", label: "Category" },
  { key: "category2", label: "Category 2" },
];

function GroupBySelector({
  value,
  onChange,
}: {
  value: SalesGroupBy;
  onChange: (groupBy: SalesGroupBy) => void;
}) {
  return (
    <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
      {groupByOptions.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
            value === key
              ? "bg-white text-gray-900 shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

interface SalesByItemUIProps {
  items: ProcessedSalesItem[];
  summary: SalesByItemSummary;
  period: SalesPeriod;
  onPeriodChange: (period: SalesPeriod) => void;
  portcoId?: string;
  loading: boolean;
  error: string | null;
}

function SalesByItemUI({
  items,
  summary,
  period,
  onPeriodChange,
  portcoId,
  loading,
  error,
}: SalesByItemUIProps) {
  const [groupBy, setGroupBy] = useState<SalesGroupBy>("item");

  // Category views only exist where the NetSuite query returns category fields
  const hasCategories = useMemo(
    () => items.some((i) => i.product_category || i.product_category_2),
    [items],
  );
  const effectiveGroupBy = hasCategories ? groupBy : "item";

  const displayItems = useMemo(
    () => groupItemsByCategory(items, effectiveGroupBy),
    [items, effectiveGroupBy],
  );

  if (loading) {
    return (
      <div className="w-full max-w-6xl space-y-6">
        <LoadingNotice message="Loading sales data — this can take a few seconds…" />
        {/* Skeleton breadcrumb */}
        <div className="flex items-center justify-between mb-2">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
        </div>
        {/* Skeleton header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-2" />
            <div className="h-4 w-48 bg-gray-100 rounded animate-pulse" />
          </div>
          <div className="h-8 w-36 bg-gray-100 rounded animate-pulse" />
        </div>
        {/* Skeleton metric cards */}
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="bg-white border border-gray-100 rounded-xl p-5"
            >
              <div className="h-3 w-20 bg-gray-100 rounded animate-pulse mx-auto mb-3" />
              <div className="h-7 w-28 bg-gray-200 rounded animate-pulse mx-auto" />
            </div>
          ))}
        </div>
        {/* Skeleton chart */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <div className="h-5 w-40 bg-gray-200 rounded animate-pulse mb-4" />
          <div className="h-96 bg-gray-50 rounded-lg animate-pulse" />
        </div>
        {/* Skeleton table */}
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
          </div>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="px-6 py-4 border-b border-gray-50 flex items-center gap-6"
            >
              <div className="flex-1 grid grid-cols-7 gap-4">
                {[...Array(7)].map((_, j) => (
                  <div
                    key={j}
                    className="h-4 bg-gray-100 rounded animate-pulse"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-lg">
        <div className="bg-white border border-red-100 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg
                  className="w-4 h-4 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-1">
                  Something went wrong
                </h3>
                <p className="text-gray-500 text-sm">{error}</p>
              </div>
            </div>
          </div>
        </div>
        {portcoId && (
          <Link
            to={`/portco/${portcoId}`}
            className="inline-flex items-center gap-1.5 mt-4 text-sm text-gray-500 hover:text-wmp-500 transition-colors no-underline"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Detail
          </Link>
        )}
      </div>
    );
  }

  const periodLabels: Record<SalesPeriod, string> = {
    mtd: "Month to Date",
    qtd: "Quarter to Date",
    ytd: "Year to Date",
  };

  return (
    <div className="w-full max-w-6xl">
      {/* Breadcrumb */}
      {/* Breadcrumb + sub-nav */}
      <div className="mb-5 space-y-3">
        <Link
          to={portcoId ? `/portco/${portcoId}` : "/dashboard"}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-wmp-500 transition-colors no-underline group"
        >
          <svg
            className="w-4 h-4 text-gray-400 group-hover:text-wmp-500 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Overview
        </Link>
        {portcoId && <PortcoSubNav portcoId={portcoId} />}
      </div>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            Sales by Item
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {periodLabels[period]} &mdash;{" "}
            {displayItems.length.toLocaleString()}{" "}
            {effectiveGroupBy === "item" ? "items" : "categories"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasCategories && (
            <GroupBySelector value={effectiveGroupBy} onChange={setGroupBy} />
          )}
          <PeriodSelector value={period} onChange={onPeriodChange} />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Total Revenue"
          value={formatCurrency(summary.totalRevenue)}
          highlighted
        />
        <MetricCard
          label="Total Cost"
          value={formatCurrency(summary.totalCost)}
        />
        <MetricCard
          label="Gross Profit"
          value={formatCurrency(summary.totalGrossProfit)}
        />
        <MetricCard
          label="Gross Margin"
          value={`${summary.totalGrossMargin.toFixed(1)}%`}
        />
      </div>

      {/* Chart */}
      {displayItems.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <svg
                className="w-5 h-5 text-wmp-500 flex-shrink-0"
                width={20}
                height={20}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <h2 className="text-lg font-semibold text-gray-900">
                {effectiveGroupBy === "item"
                  ? "Top 20 Items by Revenue"
                  : `Revenue by Product Category${effectiveGroupBy === "category2" ? " 2" : ""}`}
              </h2>
            </div>
          </div>
          <div className="p-6">
            <SalesByItemChart items={displayItems} maxItems={20} height={500} />
          </div>
        </div>
      )}

      {/* Full Table */}
      <SalesByItemTable
        items={displayItems}
        variant={effectiveGroupBy === "item" ? "item" : "category"}
      />
    </div>
  );
}

// =============================================================================
// Demo mode data loader — no MSAL hooks
// =============================================================================

function DemoSalesByItemContent() {
  const { portcoId } = useParams<{ portcoId: string }>();
  const [period, setPeriod] = useState<SalesPeriod>("ytd");
  const data = useSalesByItemData(portcoId, period);

  return (
    <SalesByItemUI
      items={data.items}
      summary={data.summary}
      period={period}
      onPeriodChange={setPeriod}
      portcoId={portcoId}
      loading={data.loading}
      error={data.error}
    />
  );
}

// =============================================================================
// Live mode data loader — uses MSAL hooks (must be inside MsalProvider)
// =============================================================================

function LiveSalesByItemContent() {
  const { portcoId } = useParams<{ portcoId: string }>();
  const { instance, inProgress } = useMsal();
  const [period, setPeriod] = useState<SalesPeriod>("ytd");

  const handleAuthError = useCallback(() => {
    instance.acquireTokenRedirect({
      ...powerbiRequest,
      account: instance.getActiveAccount()!,
    });
  }, [instance]);

  const data = useSalesByItemData(
    portcoId,
    period,
    inProgress === InteractionStatus.None,
    handleAuthError,
  );

  return (
    <SalesByItemUI
      items={data.items}
      summary={data.summary}
      period={period}
      onPeriodChange={setPeriod}
      portcoId={portcoId}
      loading={data.loading}
      error={data.error}
    />
  );
}

// =============================================================================
// Exported SalesByItem component — routes to demo or live mode
// =============================================================================

export function SalesByItem() {
  if (isDemoMode()) {
    return <DemoSalesByItemContent />;
  }

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={powerbiRequest}
      errorComponent={ErrorComponent}
      loadingComponent={Loading}
    >
      <LiveSalesByItemContent />
    </MsalAuthenticationTemplate>
  );
}
